<template>
  <!-- @click="closeVap" -->
  <div v-show="showVap" class="popVap">
    <div ref="vapRef" class="vap" :style="{ objectFit }"></div>
  </div>
</template>

<!-- 预览vap组件 -->
<script setup lang="ts">
import { ref } from 'vue'
import Vap from 'video-animation-player'
// import { showToast } from 'vant'
import type { ToastWrapperInstance } from 'vant/lib/toast/types'
import type { VapVideo, OpenVapOptions } from '@/types/vap.d.ts'
import {
  getVapJsonInfo,
  getVapJsonInfoRequestText,
  getJsonInfoByFile,
  blobToUrl
} from './ts/vap'
import common from '@/utils/common'
// import useOrigin from '@/utils/config'

/**
 * 客户端播放vap方法
 * @param url vap地址
 * @returns 是否调用成功
 */
// const appPlayVap = (url: string) => {
//   if (
//     window.androidJsObj?.showVapAnim ||
//     window.webkit?.messageHandlers?.showVapAnim
//   ) {
//     url = url.indexOf('http') === -1 ? useOrigin.VITE_APP_CDNURL + url : url
//     console.log('开始调用客户端播放vap方法, url: ', url)
//     window.appFun('showVapAnim', url)
//     return true
//   }
//   console.log('客户端vap方法不存在')
//   return false
// }

const props = defineProps({
  /**
   * 是否只播放一次，默认：是
   */
  singlePlay: {
    type: Boolean,
    default: true
  }
})
const emit = defineEmits(['onEnd', 'onStart'])
// 容器实例
const vapRef = ref<HTMLDivElement>()
// vap 实例
const vap: VapVideo = new Vap()

// 预览Vap
const showVap = ref(false)

// loading轻提示
let toast: ToastWrapperInstance | null = null
const showLoading = () => {
  // toast = showToast('Loading...')
}
const hideLoading = () => {
  if (toast) {
    toast.close()
    toast = null
  }
}

// 图像铺满方式，对应canvas的object-fit属性，默认 contain
const objectFit = ref<OpenVapOptions['objectFit']>('contain')
/**
 * 打开vap预览
 * 方法1: 没有json配置信息，需要从vap/MP4中获取json配置信息(请求一次MP4文件，会缓存)
 * 方法2: 有json配置信息，直接使用json配置信息(请求一次json文件+每次都会请求MP4文件)
 * @param options
 */
const openVap = async (options: OpenVapOptions) => {
  // 有客户端方法优先使用客户端方法
  // if (appPlayVap(options.vapUrl)) return

  if (!vapRef.value) return
  showLoading()

  // 获取json信息
  let json: { [key: string]: any } = {}
  let blob: Blob | null = null
  // 方法二 => 使用参数传入的json
  if (options.jsonConfig) {
    if (typeof options.jsonConfig === 'string') {
      // 如果json是字符串json地址，则请求json并缓存请求结果
      ;[blob, json] = await getJsonInfoByFile(
        options.jsonConfig,
        options.vapUrl
      )
    } else {
      // 否则直接使用传入的json对象
      json = options.jsonConfig
    }
  }
  // 方法一 => json配置信息从vap/MP4中获取
  else {
    // 从vap/MP4获取json信息
    console.log('ios', common.checkVersion().ios)
    if (common.checkVersion().ios) {
      // 请求类型为text(该方法播放请求两遍)
      ;[blob, json] = await getVapJsonInfoRequestText(
        options.vapUrl,
        options.startIndex,
        options.maxLength
      )
    } else {
      // 请求类型为blob(该方法播放只请求一遍MP4,但iOS不兼容)
      ;[blob, json] = await getVapJsonInfo(
        options.vapUrl,
        options.startIndex,
        options.maxLength
      )
    }
  }

  // 设置图像铺满方式
  objectFit.value = options.objectFit ? options.objectFit : 'contain'

  // 获取vap/MP4的url
  const src = blob ? await blobToUrl(blob) : options.vapUrl
  // const src = options.vapUrl;
  console.log(src)
  // 解决帧率为25时，播放没有动画只有声音的问题
  if (json?.info?.fps && ![20, 30, 60].includes(json.info.fps)) {
    json.info.fps = json.info.fps < 40 ? 30 : 60
  }

  // 播放
  vap.play({
    // 容器
    container: vapRef.value,
    // vap文件URL
    src,
    // 是否循环
    loop: !props.singlePlay,
    // 起始播放时间点
    beginPoint: 0,
    // 精准模式
    accurate: !!options.accurate,
    width: 750,
    // precache: true,
    // 设置canvas宽高全屏
    // width: window.innerWidth,
    // height: window.innerHeight,
    // 组件基于type字段做了实例化缓存，不同的VAP实例应该使用不同的type值（如0、1、2等），这里使用vapUrl作为type值
    type: options.vapUrl,
    // vap对应的json文件
    config: json as object,
    // vap融合信息
    ...(options.extend || {}),
    // 加载失败回调
    onLoadError(e) {
      console.log('onLoadError', e)
      console.log('onLoadError', e.message)
      console.log('onLoadError', e['name'])
      console.log('onLoadError', e.filename)
      console.log('onLoadError', e['code'])
      hideLoading()
      // showToast('Preview failed')
      // closeVap();
    }
  })

  // vap事件监听
  vap.once('playing', onPlaying)
  vap.once('ended', onEnded)
}

/**
 * 关闭vap
 */
const closeVap = () => {
  vap.pause()
  vap.clear()
  showVap.value = false
}

// 可以播放
const onPlaying = () => {
  hideLoading()
  // 显示
  showVap.value = true
  emit('onStart')
}
// 播放结束
const onEnded = () => {
  if (props.singlePlay) {
    closeVap()
  }
  emit('onEnd')
}

defineExpose({
  openVap
})
</script>

<style scoped lang="scss">
// 预览Vap
.popVap {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba($color: #000000, $alpha: 0.1);
  z-index: 9999;

  :deep() {
    canvas {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      height: 100%;
      object-fit: inherit;

      @include media-screen(wide) {
        max-width: none;
      }
      @include media-screen(desktop) {
        max-width: none;
      }
      @include media-screen(tablet) {
        max-width: none;
      }
      @include media-screen(tablet-mini) {
        max-width: 100%;
        object-fit: cover !important;
      }
      @include media-screen(mobile) {
        max-width: 100%;
        object-fit: cover !important;
      }
    }
  }
}
</style>
